import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, formatImageUrl } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    Plus,
    Search,
    Edit3,
    Trash2,
    CheckCircle2,
    AlertTriangle,
    Layers,
    GripVertical,
    ChevronUp,
    ChevronDown,
    ArrowUpDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminPortfolioList = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grouped'); // 'grouped', 'en', 'ka'
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}portfolios.php?admin=1`);
            const data = await response.json();
            if (Array.isArray(data)) setProjects(data);
        } catch (error) {
            console.error('Error fetching portfolios:', error);
            toast.error(t('admin.portfolioList.loadError') || 'პორტფოლიოს ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user) fetchProjects();
    }, [user, fetchProjects]);

    const deleteProject = async (id) => {
        if (!window.confirm(t('admin.portfolioList.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ პროექტის წაშლა?')) return;
        try {
            const response = await fetchWithSecurity(`${API_URL}portfolios.php?id=${id}`, { method: 'DELETE' });
            const result = await response.json();
            if (result.success) {
                toast.success(t('admin.portfolioList.deleted') || 'პროექტი წაიშალა');
                setProjects(prev => prev.filter(p => p.id !== id));
            } else {
                toast.error(result.error || 'წაშლა ვერ მოხერხდა');
            }
        } catch (error) {
            console.error('Error deleting project:', error);
            toast.error('წაშლა ვერ მოხერხდა');
        }
    };

    // Build grouped structure
    const groupedBySlug = projects.reduce((acc, item) => {
        const key = item.slug || item.id;
        if (!acc[key]) acc[key] = {};
        acc[key][item.lang || 'en'] = item;
        return acc;
    }, {});

    const groupsList = Object.entries(groupedBySlug).sort(([, a], [, b]) => {
        const orderA = Math.min(a.en?.sort_order ?? 9999, a.ka?.sort_order ?? 9999);
        const orderB = Math.min(b.en?.sort_order ?? 9999, b.ka?.sort_order ?? 9999);
        return orderA - orderB;
    });

    const filteredGroups = groupsList.filter(([slug, versions]) =>
        (versions.en?.title || versions.ka?.title || slug).toLowerCase().includes(searchTerm.toLowerCase()) ||
        slug.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredProjects = projects.filter(p => {
        const matchesLang = viewMode === 'all' || p.lang === viewMode;
        const matchesSearch =
            (p.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.slug || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.category || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesLang && matchesSearch;
    }).sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));

    // ── REORDER LOGIC FOR GROUPED VIEW ──
    const reorderGroups = async (fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= filteredGroups.length) return;

        const newGroups = [...filteredGroups];
        const [moved] = newGroups.splice(fromIndex, 1);
        newGroups.splice(toIndex, 0, moved);

        // Optimistically update projects state
        const slugOrderMap = {};
        newGroups.forEach(([slug], idx) => {
            slugOrderMap[slug] = idx + 1;
        });

        const reorderedProjects = [...projects].sort((a, b) => {
            const orderA = slugOrderMap[a.slug] ?? 9999;
            const orderB = slugOrderMap[b.slug] ?? 9999;
            return orderA - orderB;
        });
        setProjects(reorderedProjects);

        setIsSavingOrder(true);
        try {
            const groupOrders = newGroups.map(([slug], idx) => ({ slug, sort_order: idx + 1 }));
            const res = await fetchWithSecurity(`${API_URL}portfolios.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'reorder', group_orders: groupOrders })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.portfolioList.orderSaved') || 'რიგითობა წარმატებით განახლდა!');
            }
        } catch (err) {
            console.error('Reorder failed:', err);
            toast.error('თანმიმდევრობის შენახვა ვერ მოხერხდა');
            fetchProjects();
        }
        setIsSavingOrder(false);
    };

    // ── REORDER LOGIC FOR SINGLE-LANG VIEW ──
    const reorderSingleProjects = async (fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= filteredProjects.length) return;

        const newFiltered = [...filteredProjects];
        const [moved] = newFiltered.splice(fromIndex, 1);
        newFiltered.splice(toIndex, 0, moved);

        // Map order
        const orders = newFiltered.map((p, idx) => ({ id: p.id, sort_order: idx + 1 }));

        // Optimistic update
        const idOrderMap = {};
        orders.forEach(o => { idOrderMap[o.id] = o.sort_order; });

        setProjects(prev => prev.map(p => idOrderMap[p.id] ? { ...p, sort_order: idOrderMap[p.id] } : p).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));

        setIsSavingOrder(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}portfolios.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'reorder', orders })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.portfolioList.orderSaved') || 'რიგითობა წარმატებით განახლდა!');
            }
        } catch (err) {
            console.error('Reorder failed:', err);
            toast.error('თანმიმდევრობის შენახვა ვერ მოხერხდა');
            fetchProjects();
        }
        setIsSavingOrder(false);
    };

    // ── MANUAL ORDER DIRECT ENTRY ──
    const [manualOrders, setManualOrders] = useState({});

    const handleOrderInputChange = (key, val) => {
        setManualOrders(prev => ({ ...prev, [key]: val }));
    };

    const handleOrderInputCommit = (key, currentIndex, isGrouped, maxCount) => {
        const rawVal = manualOrders[key];
        if (rawVal === undefined || rawVal === '') return;

        let targetPos = parseInt(rawVal, 10);
        if (isNaN(targetPos)) {
            setManualOrders(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            return;
        }

        // Clamp between 1 and maxCount
        if (targetPos < 1) targetPos = 1;
        if (targetPos > maxCount) targetPos = maxCount;
        const targetIndex = targetPos - 1;

        setManualOrders(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });

        if (targetIndex !== currentIndex) {
            if (isGrouped) {
                reorderGroups(currentIndex, targetIndex);
            } else {
                reorderSingleProjects(currentIndex, targetIndex);
            }
        }
    };

    // Drag and Drop handlers
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        setDragOverIndex(null);
        if (draggedIndex === null || draggedIndex === undefined) return;

        if (viewMode === 'grouped') {
            reorderGroups(draggedIndex, targetIndex);
        } else {
            reorderSingleProjects(draggedIndex, targetIndex);
        }
        setDraggedIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    if (authLoading || !user) return null;

    const LangBadge = ({ lang }) => {
        if (lang === 'ka') return <span className="shadcn-badge shadcn-badge-warning">🇬🇪 KA</span>;
        return <span className="shadcn-badge shadcn-badge-info">🇬🇧 EN</span>;
    };

    return (
        <AdminLayout title={t('admin.portfolioList.title') || 'პორტფოლიო'}>
            <div className="shadcn-card">
                {/* ── CARD TOOLBAR: VIEW MODES, SEARCH & ACTION BUTTON ── */}
                <div className="d-flex flex-column flex-lg-row gap-3 mb-3 align-items-lg-center justify-content-between">
                    {/* View Mode Pills */}
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        <button
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'grouped' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('grouped')}
                        >
                            <Layers size={14} /> {t('admin.portfolioList.grouped') || 'დაჯგუფებული ხედვა'}
                        </button>
                        <button
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'en' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('en')}
                        >
                            🇬🇧 English
                        </button>
                        <button
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'ka' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('ka')}
                        >
                            🇬🇪 ქართული
                        </button>

                        {isSavingOrder && (
                            <span className="smallest text-primary d-flex align-items-center gap-1 ms-2">
                                <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
                                {t('admin.portfolioList.savingOrder') || 'რიგითობა ინახება...'}
                            </span>
                        )}
                    </div>

                    {/* Search Bar & Add Button */}
                    <div className="d-flex align-items-center gap-3 ms-lg-auto flex-wrap flex-lg-nowrap">
                        <div className="header-search-bar" style={{ width: '260px' }}>
                            <Search size={16} className="text-muted" />
                            <input
                                type="text"
                                placeholder={t('admin.portfolioList.searchPlaceholder') || 'ძებნა სათაურით, კატეგორიით ან სლაგით...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="shadcn-btn shadcn-btn-primary flex-shrink-0" onClick={() => navigate('/admin/portfolio/new')}>
                            <Plus size={16} />
                            <span>{t('admin.portfolioList.addNew') || 'ახალი პროექტის დამატება'}</span>
                        </button>
                    </div>
                </div>

                {/* ── VISUAL ORDERING HELPER NOTICE ── */}
                <div className="p-3 mb-4 rounded-3 bg-indigo bg-opacity-10 border border-primary border-opacity-20 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <ArrowUpDown size={18} className="text-primary flex-shrink-0" />
                        <span className="small text-dark">
                            <strong>{t('admin.portfolioList.orderTipTitle') || 'თანმიმდევრობის მართვა:'}</strong> {t('admin.portfolioList.orderTip') || 'ჩაწერეთ სასურველი რიგითი ნომერი (პოზიცია) პირდაპირ უჯრაში და დააჭირეთ Enter-ს, გადააადგილეთ მაუსით (Drag & Drop), ან გამოიყენეთ ისრები (↑ / ↓).'}
                        </span>
                    </div>
                    <span className="shadcn-badge shadcn-badge-info">
                        {t('admin.portfolioList.liveOrder') || 'ლაივ სინქრონიზაცია'}
                    </span>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('admin.portfolioList.loading') || 'პროექტები იტვირთება...'}</span>
                    </div>
                ) : viewMode === 'grouped' ? (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '115px' }} className="text-center">
                                        <ArrowUpDown size={14} className="me-1" />
                                        {t('admin.portfolioList.colOrder') || 'რიგი'}
                                    </th>
                                    <th>{t('admin.portfolioList.colSlug') || 'სლაგი / პროექტი'}</th>
                                    <th>{t('admin.portfolioList.colCategory') || 'კატეგორია'}</th>
                                    <th className="text-center">{t('admin.portfolioList.colEnVer') || '🇬🇧 ინგლისური ვერსია'}</th>
                                    <th className="text-center">{t('admin.portfolioList.colKaVer') || '🇬🇪 ქართული ვერსია'}</th>
                                    <th>{t('admin.portfolioList.colStatus') || 'სტატუსი'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredGroups.map(([slug, versions], index) => {
                                    const en = versions.en;
                                    const ka = versions.ka;
                                    const hasEn = !!en;
                                    const hasKa = !!ka;
                                    const isComplete = hasEn && hasKa;
                                    const preview = en || ka;
                                    const isDragging = draggedIndex === index;
                                    const isOver = dragOverIndex === index;

                                    return (
                                        <tr
                                            key={slug}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                            style={{
                                                opacity: isDragging ? 0.35 : 1,
                                                backgroundColor: isOver ? 'rgba(99, 102, 241, 0.08)' : undefined,
                                                borderTop: isOver ? '2px solid #6366f1' : undefined,
                                                transition: 'background-color 0.2s',
                                                cursor: 'grab'
                                            }}
                                            title="გადააადგილეთ (Drag) თანმიმდევრობის შესაცვლელად"
                                        >
                                            {/* Reorder Buttons & Manual Position Input */}
                                            <td className="text-center">
                                                <div 
                                                    className="d-flex align-items-center justify-content-center gap-1"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span
                                                        className="cursor-grab text-muted p-1 hover-effect"
                                                        style={{ cursor: 'grab' }}
                                                        title="გადააადგილეთ (Drag) თანმიმდევრობის შესაცვლელად"
                                                    >
                                                        <GripVertical size={15} />
                                                    </span>
                                                    <div className="position-relative" style={{ width: '46px' }}>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={filteredGroups.length}
                                                            className="form-control form-control-sm text-center fw-bold font-monospace px-1 py-0 shadow-sm"
                                                            style={{
                                                                height: '28px',
                                                                fontSize: '12px',
                                                                borderRadius: '6px',
                                                                border: manualOrders[slug] !== undefined ? '1.5px solid #6366f1' : '1px solid #cbd5e1',
                                                                background: manualOrders[slug] !== undefined ? '#eef2ff' : '#ffffff',
                                                                color: '#1e293b'
                                                            }}
                                                            value={manualOrders[slug] !== undefined ? manualOrders[slug] : index + 1}
                                                            onChange={(e) => handleOrderInputChange(slug, e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.currentTarget.blur();
                                                                } else if (e.key === 'Escape') {
                                                                    setManualOrders(prev => {
                                                                        const copy = { ...prev };
                                                                        delete copy[slug];
                                                                        return copy;
                                                                    });
                                                                    e.currentTarget.blur();
                                                                }
                                                            }}
                                                            onBlur={() => handleOrderInputCommit(slug, index, true, filteredGroups.length)}
                                                            title="ჩაწერეთ პოზიცია და დააჭირეთ Enter-ს"
                                                        />
                                                    </div>
                                                    <div className="d-flex flex-column gap-0 ms-1">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm p-0 border-0 text-muted lh-1"
                                                            disabled={index === 0}
                                                            onClick={(e) => { e.stopPropagation(); reorderGroups(index, index - 1); }}
                                                            title="აწევა ზემოთ"
                                                        >
                                                            <ChevronUp size={13} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm p-0 border-0 text-muted lh-1"
                                                            disabled={index === filteredGroups.length - 1}
                                                            onClick={(e) => { e.stopPropagation(); reorderGroups(index, index + 1); }}
                                                            title="ჩამოწევა ქვემოთ"
                                                        >
                                                            <ChevronDown size={13} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="d-flex align-items-center gap-3">
                                                    {preview?.image && (
                                                        <img
                                                            src={formatImageUrl(preview.image)}
                                                            alt=""
                                                            style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '6px' }}
                                                        />
                                                    )}
                                                    <span className="fw-bold text-dark">{slug}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="shadcn-badge shadcn-badge-primary">{preview?.category}</span>
                                            </td>
                                            <td className="text-center">
                                                {hasEn ? (
                                                    <div>
                                                        <div className="smallest text-dark fw-semibold mb-1" style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 auto' }}>{en.title}</div>
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => navigate(`/admin/portfolio/edit/${en.id}`)}>
                                                                <Edit3 size={13} />
                                                            </button>
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteProject(en.id)}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary text-success" onClick={() => navigate('/admin/portfolio/new')}>
                                                        <Plus size={12} /> {t('admin.portfolioList.addEn') || 'დაამატე EN'}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {hasKa ? (
                                                    <div>
                                                        <div className="smallest text-dark fw-semibold mb-1" style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 auto' }}>{ka.title}</div>
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => navigate(`/admin/portfolio/edit/${ka.id}`)}>
                                                                <Edit3 size={13} />
                                                            </button>
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteProject(ka.id)}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary text-warning" onClick={() => navigate('/admin/portfolio/new')}>
                                                        <Plus size={12} /> {t('admin.portfolioList.addKa') || 'დაამატე KA'}
                                                    </button>
                                                )}
                                            </td>
                                            <td>
                                                {isComplete ? (
                                                    <span className="shadcn-badge shadcn-badge-success">
                                                        <CheckCircle2 size={13} /> {t('admin.portfolioList.badgeComplete') || 'სრული'}
                                                    </span>
                                                ) : (
                                                    <span className="shadcn-badge shadcn-badge-warning">
                                                        <AlertTriangle size={13} /> {t('admin.portfolioList.missingLang', { lang: !hasEn ? 'EN' : 'KA' }) || `აკლია ${!hasEn ? 'EN' : 'KA'}`}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredGroups.length === 0 && <p className="text-center text-muted py-5 mb-0">{t('admin.portfolioList.noProjects') || 'პროექტები არ მოიძებნა.'}</p>}
                    </div>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '115px' }} className="text-center">
                                        <ArrowUpDown size={14} className="me-1" />
                                        {t('admin.portfolioList.colOrder') || 'რიგი'}
                                    </th>
                                    <th>{t('admin.portfolioList.colThumbnail') || 'მინიატურა'}</th>
                                    <th>{t('admin.portfolioList.colTitle') || 'სათაური'}</th>
                                    <th>{t('admin.portfolioList.colLang') || 'ენა'}</th>
                                    <th>{t('admin.portfolioList.colCategory') || 'კატეგორია'}</th>
                                    <th>{t('admin.portfolioList.colSlug') || 'სლაგი'}</th>
                                    <th>{t('admin.portfolioList.colActions') || 'მოქმედებები'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProjects.map((project, index) => {
                                    const isDragging = draggedIndex === index;
                                    const isOver = dragOverIndex === index;

                                    return (
                                        <tr
                                            key={project.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                            style={{
                                                opacity: isDragging ? 0.35 : 1,
                                                backgroundColor: isOver ? 'rgba(99, 102, 241, 0.08)' : undefined,
                                                borderTop: isOver ? '2px solid #6366f1' : undefined,
                                                transition: 'background-color 0.2s',
                                                cursor: 'grab'
                                            }}
                                            title="გადააადგილეთ (Drag) თანმიმდევრობის შესაცვლელად"
                                        >
                                            {/* Order controls */}
                                            <td className="text-center">
                                                <div 
                                                    className="d-flex align-items-center justify-content-center gap-1"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span
                                                        className="cursor-grab text-muted p-1 hover-effect"
                                                        style={{ cursor: 'grab' }}
                                                        title="გადააადგილეთ (Drag) თანმიმდევრობის შესაცვლელად"
                                                    >
                                                        <GripVertical size={15} />
                                                    </span>
                                                    <div className="position-relative" style={{ width: '46px' }}>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={filteredProjects.length}
                                                            className="form-control form-control-sm text-center fw-bold font-monospace px-1 py-0 shadow-sm"
                                                            style={{
                                                                height: '28px',
                                                                fontSize: '12px',
                                                                borderRadius: '6px',
                                                                border: manualOrders[project.id] !== undefined ? '1.5px solid #6366f1' : '1px solid #cbd5e1',
                                                                background: manualOrders[project.id] !== undefined ? '#eef2ff' : '#ffffff',
                                                                color: '#1e293b'
                                                            }}
                                                            value={manualOrders[project.id] !== undefined ? manualOrders[project.id] : index + 1}
                                                            onChange={(e) => handleOrderInputChange(project.id, e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.currentTarget.blur();
                                                                } else if (e.key === 'Escape') {
                                                                    setManualOrders(prev => {
                                                                        const copy = { ...prev };
                                                                        delete copy[project.id];
                                                                        return copy;
                                                                    });
                                                                    e.currentTarget.blur();
                                                                }
                                                            }}
                                                            onBlur={() => handleOrderInputCommit(project.id, index, false, filteredProjects.length)}
                                                            title="ჩაწერეთ პოზიცია და დააჭირეთ Enter-ს"
                                                        />
                                                    </div>
                                                    <div className="d-flex flex-column gap-0 ms-1">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm p-0 border-0 text-muted lh-1"
                                                            disabled={index === 0}
                                                            onClick={(e) => { e.stopPropagation(); reorderSingleProjects(index, index - 1); }}
                                                            title="აწევა ზემოთ"
                                                        >
                                                            <ChevronUp size={13} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm p-0 border-0 text-muted lh-1"
                                                            disabled={index === filteredProjects.length - 1}
                                                            onClick={(e) => { e.stopPropagation(); reorderSingleProjects(index, index + 1); }}
                                                            title="ჩამოწევა ქვემოთ"
                                                        >
                                                            <ChevronDown size={13} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <img
                                                    src={formatImageUrl(project.image, '/placeholder.jpg')}
                                                    alt=""
                                                    style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '6px' }}
                                                />
                                            </td>
                                            <td className="fw-bold text-dark">{project.title}</td>
                                            <td><LangBadge lang={project.lang} /></td>
                                            <td><span className="shadcn-badge shadcn-badge-primary">{project.category}</span></td>
                                            <td className="small text-muted">{project.slug}</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => navigate(`/admin/portfolio/edit/${project.id}`)}>
                                                        <Edit3 size={14} /> {t('common.edit') || 'რედაქტირება'}
                                                    </button>
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteProject(project.id)}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredProjects.length === 0 && <p className="text-center text-muted py-5 mb-0">{t('admin.portfolioList.noProjects') || 'პროექტები არ მოიძებნა.'}</p>}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminPortfolioList;
